// Imports.
import Swiper, {Autoplay, Controller, EffectFade, FreeMode, Keyboard, Mousewheel, Navigation, Thumbs} from 'swiper';
import 'swiper/swiper.min.css';
import 'swiper/modules/effect-fade/effect-fade.min.css';
import 'swiper/modules/navigation/navigation.min.css';

Swiper.use( [ Autoplay, Controller, EffectFade, FreeMode, Keyboard, Mousewheel, Navigation, Thumbs ] );

// Swipers.
let swipers = [];

// Fullscreen gallery.
let fsGallery;
let fsGalleryMain;
let fsGalleryThumbs;
let fsMainSwiper;
let fsThumbsSwiper;

// Reduced motion.
const reducedMotion = window.matchMedia( "(prefers-reduced-motion: reduce)" )?.matches;

const initCampaignActivitySlider = () => {
	document.querySelectorAll( '.campaign-activity-swiper' ).forEach( ( container ) => {
		const wrapper = container.querySelector( '.wp-block-post-template' );
		const slides = wrapper ? Array.from( wrapper.children ) : [];

		if ( ! wrapper || slides.length < 2 ) {
			return;
		}

		wrapper.classList.add( 'swiper-wrapper' );
		slides.forEach( ( slide ) => slide.classList.add( 'swiper-slide' ) );

		slides.forEach( ( slide ) => wrapper.append( slide.cloneNode( true ) ) );
		const loopHeight = () => wrapper.scrollHeight / 2;
		let dragging = false;
		let dragStart = 0;
		let scrollStart = 0;
		let scrollPosition = 0;
		container.scrollTop = scrollPosition;

		container.addEventListener( 'pointerdown', ( event ) => {
			dragging = true;
			dragStart = event.clientY;
			scrollStart = container.scrollTop;
			container.setPointerCapture( event.pointerId );
			container.classList.add( 'is-dragging' );
			scrollPosition = container.scrollTop;
		} );

		container.addEventListener( 'pointermove', ( event ) => {
			if ( ! dragging ) {
				return;
			}

			scrollPosition = scrollStart - ( event.clientY - dragStart );
			container.scrollTop = scrollPosition;
		} );

		const stopDragging = () => {
			dragging = false;
			container.classList.remove( 'is-dragging' );
		};

		container.addEventListener( 'pointerup', stopDragging );
		container.addEventListener( 'pointercancel', stopDragging );

		const scroll = () => {
			if ( ! dragging && ! reducedMotion ) {
				scrollPosition += .25;
				container.scrollTop = Math.floor( scrollPosition );

				if ( container.scrollTop >= loopHeight() ) {
					scrollPosition -= loopHeight();
					container.scrollTop = Math.floor( scrollPosition );
				}
			}

			window.requestAnimationFrame( scroll );
		}

		window.requestAnimationFrame( scroll );
	} );
};

/**
 * Init Swiper.
 *
 * @link https://swiperjs.com/swiper-api
 */
const initSwiper = () => {
	// Query Loop post templates are lists by default. Convert the campaign activity list
	// to Swiper slides while keeping the content server-rendered and accessible.
	document.querySelectorAll( '.campaign-activity-swiper' ).forEach( ( container ) => {
		const wrapper = container.querySelector( '.wp-block-post-template' );

		if ( ! wrapper ) {
			return;
		}

		wrapper.classList.add( 'swiper-wrapper' );
		Array.from( wrapper.children ).forEach( ( slide ) => slide.classList.add( 'swiper-slide' ) );
	} );

	initCampaignActivitySlider();

	[
		// CTA end.
		[
			'.featured .swiper',
			{
				autoplay: {
					delay: 3500,
					disableOnInteraction: false,
					enabled: ! reducedMotion
				},
				createElements: true,
				effect: "fade",
				loop: true,
				speed: 750,
			}
		],

		// Has swiper.
		[
			'.has-swiper .swiper',
			{
				autoplay: {
					delay: 3500,
					disableOnInteraction: false
				},
				createElements: true,
				effect: "fade",
				loop: true,
				speed: 750,
			}
		],

		// Galleries.
		[
			'.gallery-swiper',
			{
				autoplay: {
					delay: 5000,
					disableOnInteraction: false,
					enabled: ! reducedMotion
				},
				createElements: true,
				effect: "fade",
				loop: true
			}
		],

		[
			'.gallery-swiper-with-nav',
			{
				createElements: true,
				effect: "fade",
				loop: true,
				slidesPerView: 1,
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
			}
		],

		// Reviews.
		[
			'.reviews-swiper',
			{
				autoplay: {
					delay: 5000,
					disableOnInteraction: true
				},
				createElements: true,
				loop: true,
				slidesPerView: 1,
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
				pagination: {
					el: '.swiper-pagination',
					clickable: true,
					renderBullet: function ( index, className ) {
						let paginationEl = document.querySelector( '.swiper-pagination-elements > div:nth-of-type( ' + ( index + 1 ) + ')' );

						return paginationEl.outerHTML.replace( 'class="wp-block-button', 'class="' + className + ' wp-block-button');
					}
				},
				on: {
					slidePrevTransitionStart: function ( swiper ) {
						document.querySelectorAll( '.gallery-swiper' ).forEach( ( el ) => {
							el.swiper.autoplay.stop();

							el.swiper.slideTo( this.activeIndex );
						} );
					},
					slideNextTransitionStart: function ( swiper ) {
						document.querySelectorAll( '.gallery-swiper' ).forEach( ( el ) => {
							el.swiper.autoplay.stop();

							el.swiper.slideTo( this.activeIndex );
						} );
					},
				}
			}
		],

	].forEach( ( swiper ) => {
		let containerEl = document.querySelector( swiper[0] );

		if ( ! containerEl ) {
			return;
		}

		swiper = new Swiper( containerEl, swiper[1] );

		if ( Array.isArray( swiper ) ) {
			swipers = swipers.concat( swiper )

			return;
		}

		swipers.push( swiper );
	} );

	// Init fullscreen gallery.
	initFullscreenGallery();

	// Init intersection observer.
	initIntersectionObserver();
}

const initFullscreenGallery = () => {
	fsGallery = document.querySelector( '.fs-gallery' );

	if ( ! fsGallery ) {
		return;
	}

	let selectors = [
		'.entry-content',
		'.post-category-media',
	];

	let galleryIndex = 0;

	selectors.forEach( ( selector ) => {
		let galleries = document.querySelectorAll( selector );

		galleries.forEach( ( gallery ) => {
			galleryIndex++;

			let images = gallery.querySelectorAll( '.wp-block-gallery img:not([data-wp-on-async--click]), :scope > img:not([data-wp-on-async--click])' );

			images.forEach( ( image, imageIndex ) => {
				image.dataset.galleryId = galleryIndex;

				image.style.cursor = 'pointer';

				image.addEventListener( 'click', ( event ) => {
					if ( event.preventDefault ) {
						event.preventDefault();
					}

					openFullscreenGallery( image.dataset.galleryId, imageIndex, images );
				} );
			} );
		} );
	} );

	fsGallery.querySelector( '.btn-close' ).addEventListener( 'click', closeFullscreenGallery );

	fsGalleryMain = document.querySelector( '.fs-gallery-main' );

	fsGalleryThumbs = document.querySelector( '.fs-gallery-thumbs' );
}

const loadImagesEager = ( slideEl ) => {
	const images = slideEl.querySelectorAll( 'img[loading="lazy"]' );

	if ( images.length == 0 ) {
		return;
	}

	images.forEach(
		( img ) => {
			img.loading  = 'eager';
			img.decoding = 'async';
			img.src      = img.src;
		}
	);
}

const openFullscreenGallery = ( galleryIndex, imageIndex, images ) => {
	// Fullscreen swipers.
	if ( fsMainSwiper === undefined ) {
		const fsThumbsSwiper = new Swiper(
			'.fs-gallery-thumbs',
			{
				createElements: true,
				slidesPerView: "auto",
				slideToClickedSlide: true,
				watchSlidesProgress: true,
				mousewheel: {
					enabled: true
				},
				freeMode: {
					enabled: true,
					sticky: true
				}
			}
		);

		const fsMainSwiper = new Swiper(
			'.fs-gallery-main',
			{
				createElements: true,
				slidesPerView: 1,
				effect: "fade",
				fadeEffect: {
					crossFade: true
				},
				keyboard: {
					enabled: true
				},
				navigation: {
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev'
				},
				pagination: {
					el: ".swiper-pagination",
					type: "fraction"
				},
				thumbs: {
					swiper: fsThumbsSwiper
				},
				on: {
					slideChange( sw ) {
						[0, 1, -1, 2, -2].forEach( offset => {
							let slideIndex = sw.activeIndex + offset;

							const slide = sw.slides[ slideIndex ];

							if ( slide ) {
								loadImagesEager( slide );
							}
						} );
					}
				}
			}
		);

		swipers.push( fsThumbsSwiper );
		swipers.push( fsMainSwiper );
	}

	if ( galleryIndex !== fsGallery.dataset.galleryIndex ) {
		fsGallery.dataset.galleryIndex = galleryIndex;

		fsGallery.querySelectorAll( '.swiper-slide' ).forEach( slide => slide.remove() );

		let mainWrapper = fsGalleryMain.querySelector( '.swiper-wrapper' );
		let thumbsWrapper = fsGalleryThumbs.querySelector( '.swiper-wrapper' );

		images.forEach( ( image, index ) => {
			// Add thumbnail slide.
			let thumbSlide = document.createElement( 'div' );

			thumbSlide.className = 'swiper-slide';
			
			thumbSlide.append( image.cloneNode() );

			thumbsWrapper.append( thumbSlide );

			// Add gallery slide.
			let gallerySlide = document.createElement( 'div' );

			gallerySlide.className = 'swiper-slide';

			imageClone = image.cloneNode();

			imageClone.removeAttribute( 'width' );
			imageClone.removeAttribute( 'height' );

			imageClone.classList.remove( 'attachment-thumbnail' );
			imageClone.classList.remove( 'size-thumbnail' );

			imageClone.setAttribute( 'decoding', 'async' );

			if ( imageClone.dataset.fullSrc ) {
				imageClone.setAttribute( 'src', imageClone.dataset.fullSrc );

				imageClone.removeAttribute( 'data-full-src' );
			}

			if ( imageClone.dataset.fullSizes ) {
				imageClone.setAttribute( 'sizes', imageClone.dataset.fullSizes );

				imageClone.removeAttribute( 'data-full-sizes' );
			}

			if ( imageClone.dataset.fullSrcset ) {
				imageClone.setAttribute( 'srcset', imageClone.dataset.fullSrcset );

				imageClone.removeAttribute( 'data-full-srcset' );
			}

			let loadingStrategy = 'lazy';

			if ( imageIndex === index ) {
				loadingStrategy = 'eager';
			}

			imageClone.setAttribute( 'loading', loadingStrategy );

			gallerySlide.append( imageClone );

			mainWrapper.append( gallerySlide );
		} );

		fsGalleryMain.swiper.update();
		fsGalleryThumbs.swiper.update();
	}

	// Slide to	index.
	fsGalleryMain.swiper.slideTo( imageIndex );
	fsGalleryThumbs.swiper.slideTo( imageIndex );

	// Show fullscreen gallery.
	fsGallery.classList.add( 'showing' );

	document.querySelector( 'html' ).classList.add( 'has-open-modal' );

	// Add event listener to close gallery with 'Escape'.
	window.addEventListener( 'keydown', keyboardCloseFullscreenGallery );
};

const closeFullscreenGallery = () => {
	// Close fullscreen gallery.
	document.querySelector( 'html' ).classList.remove( 'has-open-modal' );

	document.querySelector( '.fs-gallery' ).classList.remove( 'showing' );

	// Remove key down event listener.
	window.removeEventListener( 'keydown', keyboardCloseFullscreenGallery );
};

const keyboardCloseFullscreenGallery = ( event ) => {
	// Check 'Escape' key.
	if ( event.keyCode !== 27 ) {
		return;
	}

	closeFullscreenGallery();
};

/**
 * Intersection observer to play slideshows only when in viewport.
 */
const intersectionObserver = reducedMotion ? false : new IntersectionObserver(
	( entries ) => {
		entries.forEach( ( entry ) => {
			let swiper = swipers.find( ( instance ) => instance.el === entry.target );

			if ( ! swiper ) {
				return;
			}

			if ( ! entry.isIntersecting ) {
				if ( ! swiper.params.autoplay.enabled || ! swiper.params.autoplay.delay ) {
					return;
				}

				// Stop autoplay.
				swiper.autoplay.stop();

				return;
			}

			// Start.
			swiper.autoplay.start();
		} );
	}
);

/**
 * Init intersection observer.
 *
 * @return void
 */
const initIntersectionObserver = () => {
	if ( ! intersectionObserver ) {
		return;
	}

	// Disconnect intersection observer.
	intersectionObserver.disconnect();

	swipers.forEach( ( swiper ) => {
		if ( ! swiper.params.autoplay.enabled || ! swiper.params.autoplay.delay ) {
			return;
		}

		// Observe intersection.
		intersectionObserver.observe( swiper.el );
	} );
}

/**
 * Stop all swipers.
 *
 * @return void
 */
const stopAllSwipers = () => {
	// Disconnect intersection observer.
	if ( intersectionObserver ) {
		intersectionObserver.disconnect();
	}

	// Stop autoplay.
	swipers.forEach( ( swiper ) => {
		if ( ! swiper.params.autoplay.enabled || ! swiper.params.autoplay.delay ) {
			return;
		}

		swiper.autoplay.stop();
	} );
}

/**
 * Event listeners.
 */
document.addEventListener( 'DOMContentLoaded', initSwiper );
window.addEventListener( 'focus', initIntersectionObserver );
window.addEventListener( 'blur', stopAllSwipers );

window.addEventListener( 'blur', function() {
	window.addEventListener( 'scroll', initIntersectionObserver, { once: true } );
} );
